import { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Skeleton,
  IconButton,
  Divider,
} from '@mui/material';
import { X, Send, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../services/api';
import {
  STATUS_OPTIONS,
  PRIORITY_OPTIONS,
  STATUS_COLORS,
  PRIORITY_COLORS,
} from '../../constants/feedback';
import { formatDate } from '../../utils/formatDate';

export default function FeedbackDetailModal({ open, feedbackUuid, onClose, onUpdated }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [commenting, setCommenting] = useState(false);

  const { control: updateControl, handleSubmit: handleUpdateSubmit, reset: resetUpdate } = useForm({
    defaultValues: { status: '', priority: '' },
  });

  const { control: commentControl, handleSubmit: handleCommentSubmit, reset: resetComment, watch } = useForm({
    defaultValues: { comment: '' },
  });

  const commentValue = watch('comment');

  // ── Data fetching ──────────────────────────────────────────────────────────

  const fetchDetail = async () => {
    if (!feedbackUuid) return;
    setLoading(true);
    try {
      const data = await api.admin.getFeedback(feedbackUuid);
      setDetail(data);
      resetUpdate({
        status: data.status || '',
        priority: data.priority || '',
      });
    } catch (error) {
      console.error('Failed to fetch feedback detail:', error);
      toast.error('Failed to load feedback details');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && feedbackUuid) {
      fetchDetail();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, feedbackUuid]);

  // ── Event handlers ─────────────────────────────────────────────────────────

  const handleClose = () => {
    if (!updating && !commenting) {
      setDetail(null);
      resetComment();
      onClose();
    }
  };

  const onUpdateSubmit = async (data) => {
    const payload = {};
    if (data.status) payload.status = data.status;
    if (data.priority) payload.priority = data.priority;

    if (Object.keys(payload).length === 0) {
      toast.error('Please select at least one field to update');
      return;
    }

    setUpdating(true);
    try {
      await api.admin.updateFeedback(feedbackUuid, payload);
      toast.success('Feedback updated successfully');
      await fetchDetail();
      onUpdated();
    } catch (error) {
      console.error('Failed to update feedback:', error);
      toast.error(error.message || 'Failed to update feedback');
    } finally {
      setUpdating(false);
    }
  };

  const onCommentSubmit = async (data) => {
    if (!data.comment.trim()) return;
    setCommenting(true);
    try {
      await api.admin.addFeedbackComment(feedbackUuid, data.comment.trim());
      toast.success('Comment added');
      resetComment();
      await fetchDetail();
    } catch (error) {
      console.error('Failed to add comment:', error);
      toast.error(error.message || 'Failed to add comment');
    } finally {
      setCommenting(false);
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <div className="flex justify-between items-center">
          <span>Feedback Details</span>
          <IconButton onClick={handleClose} disabled={updating || commenting} size="small">
            <X className="w-5 h-5" />
          </IconButton>
        </div>
      </DialogTitle>

      <DialogContent dividers>
        {loading ? (
          <div className="space-y-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} height={32} />
            ))}
          </div>
        ) : !detail ? (
          <p className="text-gray-500 text-center py-4">No details available.</p>
        ) : (
          <div className="space-y-6">
            {/* Meta info */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Title</p>
                <p className="font-medium text-gray-900">{detail.title}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Category</p>
                <div className="flex items-center gap-2">
                  <p className="font-medium text-gray-900 capitalize">{detail.category?.replace(/_/g, ' ') || 'N/A'}</p>
                  {detail.session_id && (
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<ExternalLink className="w-3.5 h-3.5" />}
                      onClick={() => window.open(`/chat/${detail.session_id}`, '_blank')}
                      sx={{ textTransform: 'none', fontSize: '0.75rem', py: 0.25, px: 1 }}
                    >
                      View Chat
                    </Button>
                  )}
                </div>
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Status</p>
                <Chip
                  label={STATUS_OPTIONS.find((s) => s.value === detail.status)?.label || detail.status}
                  color={STATUS_COLORS[detail.status] || 'default'}
                  size="small"
                />
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Priority</p>
                {detail.priority ? (
                  <Chip
                    label={PRIORITY_OPTIONS.find((p) => p.value === detail.priority)?.label || detail.priority}
                    color={PRIORITY_COLORS[detail.priority] || 'default'}
                    size="small"
                  />
                ) : (
                  <span className="text-gray-400 text-sm">Not set</span>
                )}
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Submitted By</p>
                <p className="text-sm text-gray-900">{detail.user_display_name || 'N/A'}</p>
                <p className="text-xs text-gray-500">{detail.user_email}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Created At</p>
                <p className="text-sm text-gray-900">{formatDate(detail.created_at, { includeTime: true })}</p>
              </div>
            </div>

            {/* Description */}
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Description</p>
              <p className="text-sm text-gray-800 whitespace-pre-wrap bg-gray-50 rounded p-3">{detail.description || 'No description provided.'}</p>
            </div>

            {/* Images */}
            {detail.images && detail.images.length > 0 && (
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wide mb-2">Attachments</p>
                <div className="flex flex-wrap gap-2">
                  {detail.images.map((img, idx) => (
                    <a
                      key={idx}
                      href={img}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-blue-600 underline"
                    >
                      Image {idx + 1}
                    </a>
                  ))}
                </div>
              </div>
            )}

            <Divider />

            {/* Update status/priority */}
            <div>
              <p className="text-sm font-semibold text-gray-700 mb-3">Update Feedback</p>
              <form onSubmit={handleUpdateSubmit(onUpdateSubmit)} className="flex flex-wrap gap-3 items-end">
                <Controller
                  name="status"
                  control={updateControl}
                  render={({ field }) => (
                    <FormControl size="small" sx={{ minWidth: 160 }}>
                      <InputLabel>Status</InputLabel>
                      <Select {...field} label="Status" disabled={updating}>
                        <MenuItem value=""><em>No change</em></MenuItem>
                        {STATUS_OPTIONS.map((s) => (
                          <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                />
                <Controller
                  name="priority"
                  control={updateControl}
                  render={({ field }) => (
                    <FormControl size="small" sx={{ minWidth: 140 }}>
                      <InputLabel>Priority</InputLabel>
                      <Select {...field} label="Priority" disabled={updating}>
                        <MenuItem value=""><em>No change</em></MenuItem>
                        {PRIORITY_OPTIONS.map((p) => (
                          <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                />
                <Button type="submit" variant="contained" size="small" disabled={updating}>
                  {updating ? 'Saving...' : 'Save Changes'}
                </Button>
              </form>
            </div>

            <Divider />

            {/* Status Log */}
            {detail.status_log && detail.status_log.length > 0 && (
              <div>
                <p className="text-sm font-semibold text-gray-700 mb-2">Status History</p>
                <div className="space-y-2">
                  {detail.status_log.map((log, idx) => (
                    <div key={idx} className="flex items-center gap-2 text-sm text-gray-600">
                      <span className="text-gray-400 text-xs">{formatDate(log.created_at, { includeTime: true })}</span>
                      <span>
                        {log.old_status ? (
                          <>
                            <span className="capitalize">{log.old_status.replace(/_/g, ' ')}</span>
                            {' → '}
                          </>
                        ) : 'Set to '}
                        <span className="font-medium capitalize">{log.new_status.replace(/_/g, ' ')}</span>
                      </span>
                      <span className="text-gray-400">by {log.changed_by_name}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <Divider />

            {/* Comments */}
            <div>
              <p className="text-sm font-semibold text-gray-700 mb-3">
                Comments ({detail.comments?.length || 0})
              </p>
              {detail.comments && detail.comments.length > 0 ? (
                <div className="space-y-3 mb-4 max-h-60 overflow-y-auto">
                  {detail.comments.map((c) => (
                    <div
                      key={c.uuid}
                      className={`rounded-lg p-3 text-sm ${
                        c.is_admin
                          ? 'bg-blue-50 border border-blue-100'
                          : 'bg-gray-50 border border-gray-100'
                      }`}
                    >
                      <div className="flex justify-between items-center mb-1">
                        <span className="font-medium text-gray-900">
                          {c.user_display_name}
                          {c.is_admin && (
                            <Chip label="Admin" size="small" color="primary" sx={{ ml: 1, height: 18, fontSize: 10 }} />
                          )}
                        </span>
                        <span className="text-xs text-gray-400">{formatDate(c.created_at, { includeTime: true })}</span>
                      </div>
                      <p className="text-gray-700 whitespace-pre-wrap">{c.comment}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-400 text-sm mb-4">No comments yet.</p>
              )}

              {/* Add comment form */}
              <form onSubmit={handleCommentSubmit(onCommentSubmit)} className="flex gap-2">
                <Controller
                  name="comment"
                  control={commentControl}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      placeholder="Add an admin comment..."
                      size="small"
                      fullWidth
                      multiline
                      maxRows={4}
                      disabled={commenting}
                    />
                  )}
                />
                <Button
                  type="submit"
                  variant="contained"
                  size="small"
                  disabled={commenting || !commentValue?.trim()}
                  sx={{ minWidth: 40, px: 1.5 }}
                >
                  {commenting ? '...' : <Send className="w-4 h-4" />}
                </Button>
              </form>
            </div>
          </div>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={updating || commenting}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
